import { useMemo } from 'react'

import useQuoteResultCosts from 'components/hooks/quotes/useQuoteResultCosts'
import { useQuotingEmployeeContext } from 'components/quoting/employees/QuotingEmployeeContext'
import { type QuotingQuoteResult } from 'utils/web_api.schemas'

import ChecklistCard from './ChecklistCard'
import { PreliminaryEstimateCard } from './PreliminaryEstimateCard'
import { QuoteExplorerMode } from './QuoteExplorer'
import { TotalCostBreakdownCard } from './TotalCostBreakdownCard'

type Props = {
  employees?: QuotingQuoteResult[]
  employerEmployeeContribution: number
  employerSpouseContribution: number
  employerChildContribution: number
  isLoading: boolean
  mode: QuoteExplorerMode
}

export default function SidePanelContent ({
  employees = [],
  employerEmployeeContribution,
  employerSpouseContribution,
  employerChildContribution,
  isLoading,
  mode,
}: Props) {
  const isEditMode = mode === QuoteExplorerMode.edit

  const hasCurrentCoverageData = useMemo(() => {
    const numEmployeesWithCurrentCoverageData = employees?.filter((result) => result.employee_medical_contribution !== null && result.employer_medical_contribution !== null).length ?? 0
    return (numEmployeesWithCurrentCoverageData / employees.length) >= 0.5
  }, [employees])

  const { willEnrollEmployeeIds } = useQuotingEmployeeContext()

  const willEnrollEmployees = useMemo(() => {
    return employees.filter((employee) => willEnrollEmployeeIds.has(employee.id))
  }, [employees, willEnrollEmployeeIds])

  const {
    totalMonthlyEmployeeContribution,
    totalMonthlyEmployerCost,
    currentPlanTotalEmployeeMedicalContribution,
    currentPlanTotalEmployerMedicalContribution,
  } = useQuoteResultCosts(willEnrollEmployees, employerEmployeeContribution, employerSpouseContribution, employerChildContribution)

  return (
    <>
      <TotalCostBreakdownCard
        isLoading={isLoading}
        currentPlanTotalEmployeeMedicalContribution={currentPlanTotalEmployeeMedicalContribution}
        currentPlanTotalEmployerMedicalContribution={currentPlanTotalEmployerMedicalContribution}
        totalMonthlyEmployeeContribution={totalMonthlyEmployeeContribution}
        totalMonthlyEmployerCost={totalMonthlyEmployerCost}
        shouldDisplayComparisons={hasCurrentCoverageData}
      />
      {isEditMode && (
        <ChecklistCard
          isLoading={isLoading}
          quoteResults={employees}
          employerEmployeeContribution={employerEmployeeContribution}
          employerSpouseContribution={employerSpouseContribution}
          employerChildContribution={employerChildContribution}
        />
      )}
      <PreliminaryEstimateCard />
    </>
  )
}
